/**
 * RBAC Option B — Lightweight permissions layer.
 *
 * Single source of truth for what each role/flag can do, in code (no DB).
 * The backend mirror is `apps/core/permissions.py`'s PERMISSIONS / ROLE_PERMISSIONS
 * block — keep them in lockstep when adding entries here.
 *
 * Frontend usage:
 *   const canViewPastPapers = usePermission(PERMISSIONS.VIEW_PAST_PAPER_QUESTIONS);
 *   {canViewPastPapers && <PastPaperBlock />}
 *
 * Treat the frontend gate as a UX hint, not a security boundary — the backend
 * decorator/permission class is the actual gate. See docs/RBAC_DESIGN.md §3.
 */

/** Named atomic capabilities. Add here, then mirror in the backend. */
export const PERMISSIONS = {
  // Wiki / Curriculum
  VIEW_CURRICULUM_REFERENCE: 'view_curriculum_reference',
  VIEW_DRAFT_WIKI_PAGES:     'view_draft_wiki_pages',
  VIEW_PAST_PAPER_QUESTIONS: 'view_past_paper_questions',
  VIEW_ALL_GRADES:           'view_all_grades',

  // Subjects (per-school catalogue)
  MANAGE_SUBJECTS:           'manage_subjects',

  // Assessments / Rubrics
  CREATE_ASSESSMENT:         'create_assessment',
  GRADE_SUBMISSION:          'grade_submission',
  EDIT_RUBRIC:               'edit_rubric',
  VIEW_CLASS_RESULTS:        'view_class_results',

  // PAT (Practical Assessment Task) — IEB Gr12 multi-phase assessment
  EDIT_PAT_ASSESSMENT:       'edit_pat_assessment',
  GENERATE_PAT_REPORT:       'generate_pat_report',
  MODERATE_PAT_SUBMISSIONS:  'moderate_pat_submissions',

  // External integrations
  MANAGE_GITHUB_INTEGRATION: 'manage_github_integration',
  MANAGE_MOODLE_INTEGRATION: 'manage_moodle_integration',

  // Cluster collaboration (cross-school teacher groups)
  MANAGE_CLUSTERS:           'manage_clusters',

  // Reporting outputs (term comments + LaTeX reports for parents)
  GENERATE_COMMENTS:         'generate_comments',
  GENERATE_REPORTS:          'generate_reports',

  // Teacher's own dashboard + data lifecycle (export, cleanup, history)
  VIEW_TEACHER_DASHBOARD:    'view_teacher_dashboard',
  MANAGE_OWN_DATA:           'manage_own_data',

  // Test external service configurations (AI, ADAM)
  TEST_INTEGRATIONS:         'test_integrations',

  // Class / teacher / learner management
  MANAGE_OWN_CLASSES:        'manage_own_classes',
  MANAGE_TEACHERS:           'manage_teachers',
  UPDATE_TEACHER_PROFILE:    'update_teacher_profile',
  MANAGE_LEARNERS:           'manage_learners',

  // Learner-facing capabilities (a student viewing their own data)
  VIEW_OWN_LEARNER_DATA:     'view_own_learner_data',

  // School / admin
  VIEW_ADMIN_SETTINGS:       'view_admin_settings',
  MANAGE_SCHOOL:             'manage_school',
  IMPERSONATE_LEARNER:       'impersonate_learner',

  // Communications (Sooth WhatsApp integration)
  VIEW_SOOTH_INBOX:          'view_sooth_inbox',
  MANAGE_SOOTH_CONNECTORS:   'manage_sooth_connectors',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Role → set of permissions. Admin has the full set (computed dynamically
 * from PERMISSIONS so adding a new permission auto-grants it to admin).
 */
const TEACHER_PERMS: Set<Permission> = new Set([
  PERMISSIONS.VIEW_CURRICULUM_REFERENCE,
  PERMISSIONS.VIEW_DRAFT_WIKI_PAGES,
  PERMISSIONS.VIEW_PAST_PAPER_QUESTIONS,
  PERMISSIONS.VIEW_ALL_GRADES,
  PERMISSIONS.CREATE_ASSESSMENT,
  PERMISSIONS.GRADE_SUBMISSION,
  PERMISSIONS.EDIT_RUBRIC,
  PERMISSIONS.VIEW_CLASS_RESULTS,
  PERMISSIONS.IMPERSONATE_LEARNER,
  PERMISSIONS.UPDATE_TEACHER_PROFILE,
  PERMISSIONS.MANAGE_LEARNERS,
  PERMISSIONS.VIEW_SOOTH_INBOX,
  PERMISSIONS.MANAGE_SOOTH_CONNECTORS,
  PERMISSIONS.EDIT_PAT_ASSESSMENT,
  PERMISSIONS.GENERATE_PAT_REPORT,
  PERMISSIONS.MODERATE_PAT_SUBMISSIONS,
  PERMISSIONS.MANAGE_GITHUB_INTEGRATION,
  PERMISSIONS.MANAGE_MOODLE_INTEGRATION,
  PERMISSIONS.MANAGE_CLUSTERS,
  PERMISSIONS.GENERATE_COMMENTS,
  PERMISSIONS.GENERATE_REPORTS,
  PERMISSIONS.VIEW_TEACHER_DASHBOARD,
  PERMISSIONS.MANAGE_OWN_DATA,
  PERMISSIONS.TEST_INTEGRATIONS,
  PERMISSIONS.MANAGE_OWN_CLASSES,
]);

const SCHOOL_ADMIN_PERMS: Set<Permission> = new Set([
  ...Array.from(TEACHER_PERMS),
  PERMISSIONS.MANAGE_SUBJECTS,
  PERMISSIONS.MANAGE_SCHOOL,
  PERMISSIONS.VIEW_ADMIN_SETTINGS,
  PERMISSIONS.MANAGE_TEACHERS,
]);

const STUDENT_PERMS: Set<Permission> = new Set([
  PERMISSIONS.VIEW_CURRICULUM_REFERENCE,
  PERMISSIONS.VIEW_OWN_LEARNER_DATA,
]);

/**
 * Consultant is a flag on Teacher (not a UserRole), and grants extra
 * permissions on top of the user's underlying role. This lets a consultant
 * be either a teacher or a school admin and gain consultant capabilities
 * without role-juggling.
 */
const CONSULTANT_EXTRA_PERMS: Set<Permission> = new Set([
  PERMISSIONS.MANAGE_SUBJECTS,
]);

interface PermissionContext {
  isTeacher: boolean;
  isAdmin: boolean;
  isSchoolAdmin: boolean;
  isConsultant: boolean;
}

/**
 * Resolve a single permission against a user's role flags.
 * The hook `usePermission` is the normal call site; this function is
 * exported separately for non-component contexts (utility code, tests).
 *
 * Resolution order:
 *   1. Admin → all permissions.
 *   2. School-admin → SCHOOL_ADMIN_PERMS.
 *   3. Teacher → TEACHER_PERMS.
 *   4. Consultant flag → adds CONSULTANT_EXTRA_PERMS on top of (2) or (3).
 *   5. Default → STUDENT_PERMS.
 */
export function hasPermission(perm: Permission, ctx: PermissionContext): boolean {
  if (ctx.isAdmin) return true;
  if (ctx.isSchoolAdmin && SCHOOL_ADMIN_PERMS.has(perm)) return true;
  if (ctx.isTeacher && TEACHER_PERMS.has(perm)) return true;
  if (ctx.isConsultant && CONSULTANT_EXTRA_PERMS.has(perm)) return true;
  if (!ctx.isTeacher && !ctx.isSchoolAdmin && !ctx.isAdmin) {
    return STUDENT_PERMS.has(perm);
  }
  return false;
}
